/**
 * Contexto de apertura del estudio — ramifica persistencia de «Adjuntar PDF / informe».
 * No confundir con studyLoadSource (chip de descarga).
 */

import { getPacsConfig } from '../config/pacsConfig'
import { isStudyLoadLocal } from './studyLoadSource'

export const STUDY_OPEN_MODE = {
  ORTHANC: 'orthanc',
  LEGACY_WADO: 'legacy_wado',
  TICKET_R2: 'ticket_r2',
  TICKET_EDGE: 'ticket_edge',
  LOCAL: 'local',
}

export const STUDY_WRITE_KIND = {
  ORTHANC_CASCADE: 'orthanc_cascade',
  STOW_WADO: 'stow_wado',
  NONE: 'none',
  /** Futuro: POST ticketApi …/reports (contrato plataforma). */
  PLATFORM_REPORT: 'platform_report',
}

export const STUDY_REFRESH_STRATEGY = {
  ORTHANC_RELOAD: 'orthanc_reload',
  RE_QIDO: 're_qido',
  LOCAL_INJECT: 'local_inject',
  NONE: 'none',
}

/**
 * @typedef {{
 *   kind: string,
 *   pacsConfig?: object|null,
 *   wadoUrl?: string|null,
 *   dicomWebBase?: string|null,
 * }} StudyWriteTarget
 *
 * @typedef {{
 *   mode: string,
 *   writeTarget: StudyWriteTarget|null,
 *   refreshStrategy: string,
 *   ticketId?: string|null,
 *   canPersistRemotely: boolean,
 * }} StudyOpenContext
 */

/**
 * @param {object} signals
 * @param {string|null} [signals.ticketIdFromUrl]
 * @param {string|null} [signals.manifestUrlFromUrl]
 * @param {object|null} [signals.legacyWadoContext]
 * @param {string|null} [signals.studyIdFromUrl]
 * @param {string|null} [signals.pacsIdFromUrl]
 * @param {string|null} [signals.studyLoadSource]
 * @returns {StudyOpenContext}
 */
export function resolveStudyOpenContext(signals = {}) {
  const ticketId = signals.ticketIdFromUrl
    ? String(signals.ticketIdFromUrl)
    : null
  const manifestUrl = signals.manifestUrlFromUrl
    ? String(signals.manifestUrlFromUrl)
    : null
  const legacy = signals.legacyWadoContext || null
  const studyId = signals.studyIdFromUrl
    ? String(signals.studyIdFromUrl)
    : null
  const pacsId = signals.pacsIdFromUrl || null
  const loadSource = signals.studyLoadSource || null

  const pacsConfig = pacsId ? getPacsConfig(pacsId) : null

  // Ticket + Path M / R2 manifest (prioridad sobre Orthanc studyId)
  if (ticketId && manifestUrl && !(legacy && legacy.wadoUrl)) {
    return {
      mode: STUDY_OPEN_MODE.TICKET_R2,
      writeTarget: {
        kind: STUDY_WRITE_KIND.PLATFORM_REPORT,
        pacsConfig: null,
        wadoUrl: null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.LOCAL_INJECT,
      ticketId,
      canPersistRemotely: false,
    }
  }

  // Ticket + edge/dicomweb (legacyWadoContext armado desde candidate)
  if (ticketId && legacy && legacy.wadoUrl) {
    return {
      mode: STUDY_OPEN_MODE.TICKET_EDGE,
      writeTarget: {
        kind: STUDY_WRITE_KIND.STOW_WADO,
        pacsConfig: pacsConfig || getPacsConfig(pacsId),
        wadoUrl: legacy.wadoUrl,
        dicomWebBase: legacy.qidoBase || null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.RE_QIDO,
      ticketId,
      canPersistRemotely: true,
    }
  }

  // Deep-link legacy WADO (sin ticket)
  if (legacy && legacy.wadoUrl && !ticketId) {
    return {
      mode: STUDY_OPEN_MODE.LEGACY_WADO,
      writeTarget: {
        kind: STUDY_WRITE_KIND.STOW_WADO,
        pacsConfig: pacsConfig || getPacsConfig(pacsId),
        wadoUrl: legacy.wadoUrl,
        dicomWebBase: legacy.qidoBase || null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.RE_QIDO,
      ticketId: null,
      canPersistRemotely: true,
    }
  }

  // Orthanc studyId
  if (studyId) {
    return {
      mode: STUDY_OPEN_MODE.ORTHANC,
      writeTarget: {
        kind: STUDY_WRITE_KIND.ORTHANC_CASCADE,
        pacsConfig: pacsConfig || getPacsConfig(pacsId),
        wadoUrl: null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.ORTHANC_RELOAD,
      ticketId,
      canPersistRemotely: true,
    }
  }

  // Manifest R2 sin ticket (deep-link manifestUrl) — no STOW hub
  if (manifestUrl) {
    return {
      mode: STUDY_OPEN_MODE.TICKET_R2,
      writeTarget: {
        kind: STUDY_WRITE_KIND.PLATFORM_REPORT,
        pacsConfig: null,
        wadoUrl: null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.LOCAL_INJECT,
      ticketId: null,
      canPersistRemotely: false,
    }
  }

  // Local / default
  if (isStudyLoadLocal(loadSource) || !studyId) {
    return {
      mode: STUDY_OPEN_MODE.LOCAL,
      writeTarget: {
        kind: STUDY_WRITE_KIND.NONE,
        pacsConfig: null,
        wadoUrl: null,
      },
      refreshStrategy: STUDY_REFRESH_STRATEGY.LOCAL_INJECT,
      ticketId: null,
      canPersistRemotely: false,
    }
  }

  return {
    mode: STUDY_OPEN_MODE.LOCAL,
    writeTarget: { kind: STUDY_WRITE_KIND.NONE, pacsConfig: null, wadoUrl: null },
    refreshStrategy: STUDY_REFRESH_STRATEGY.LOCAL_INJECT,
    ticketId: null,
    canPersistRemotely: false,
  }
}

/**
 * ¿El mode permite cascada STOW/Orthanc local?
 * @param {StudyOpenContext|null|undefined} ctx
 * @returns {boolean}
 */
export function studyOpenContextAllowsPacsStore(ctx) {
  if (!ctx || !ctx.writeTarget) return false
  const k = ctx.writeTarget.kind
  return (
    k === STUDY_WRITE_KIND.ORTHANC_CASCADE || k === STUDY_WRITE_KIND.STOW_WADO
  )
}

/**
 * Merge file item de informe en pool sin duplicar seriesInstanceUID.
 * @param {object[]} existing
 * @param {object} pdfItem
 * @returns {object[]}
 */
export function mergeAttachedReportIntoFiles(existing, pdfItem) {
  const list = Array.isArray(existing) ? existing.slice() : []
  if (!pdfItem) return list
  const seriesUid = pdfItem.seriesInstanceUID
    ? String(pdfItem.seriesInstanceUID)
    : ''
  const sop = pdfItem.sopInstanceUID ? String(pdfItem.sopInstanceUID) : ''
  const filtered = list.filter((f) => {
    if (!f) return false
    if (sop && f.sopInstanceUID && String(f.sopInstanceUID) === sop) {
      return false
    }
    if (
      seriesUid &&
      f.seriesInstanceUID &&
      String(f.seriesInstanceUID) === seriesUid
    ) {
      return false
    }
    if (
      seriesUid &&
      f.series &&
      f.series.seriesInstanceUID &&
      String(f.series.seriesInstanceUID) === seriesUid
    ) {
      return false
    }
    return true
  })
  filtered.push(pdfItem)
  return filtered
}
